// Volt, AI-makkeren: en rund petrol robot med gule LED-øjne, headset og en kabelhale, der ender i
// et stik. Tegnes med Graphics og bages ind i atlasset som alle andre figurer. Panelet i DOM'en
// bruger de samme teksturer som billeder (Atlas.image), så Volt ser ens ud på kortet og i panelet.

import type { Graphics } from 'pixi.js';
import { C, mix } from './palette';

const OUT = C.ink;
const OW = 2.2;

type Draw = (g: Graphics) => void;
type Eyes = 'happy' | 'think' | 'worried' | 'blink';

/** Kabelhalen: en blød S-kurve ud til højre med et lille stik for enden. */
function tail(g: Graphics): void {
  g.moveTo(14, 16).bezierCurveTo(28, 22, 22, 34, 34, 34);
  g.stroke({ width: 5.5, color: OUT, cap: 'round' });
  g.moveTo(14, 16).bezierCurveTo(28, 22, 22, 34, 34, 34);
  g.stroke({ width: 3, color: C.petrol, cap: 'round' });
  g.roundRect(33, 30.5, 7, 7, 1.6).fill(C.yellow).stroke({ width: 1.6, color: OUT });
  g.moveTo(40, 32.5).lineTo(43, 32.5).moveTo(40, 35.5).lineTo(43, 35.5).stroke({ width: 1.5, color: OUT, cap: 'round' });
}

function ledEyes(g: Graphics, eyes: Eyes): void {
  for (const sx of [-7.5, 7.5]) {
    // Glød bag øjet.
    g.circle(sx, -2, 6).fill({ color: C.yellow, alpha: 0.22 });
    if (eyes === 'blink') {
      g.moveTo(sx - 3.6, -2).lineTo(sx + 3.6, -2).stroke({ width: 2.4, color: C.yellow, cap: 'round' });
    } else if (eyes === 'happy') {
      g.moveTo(sx - 3.8, -0.5).quadraticCurveTo(sx, -6.5, sx + 3.8, -0.5).stroke({ width: 2.8, color: C.yellow, cap: 'round' });
    } else if (eyes === 'worried') {
      g.ellipse(sx, -1.5, 3, 3.6).fill(C.yellow);
      g.moveTo(sx - 4, -8 + (sx < 0 ? 1.5 : 0)).lineTo(sx + 4, -8 + (sx < 0 ? 0 : 1.5)).stroke({ width: 1.8, color: C.yellow, cap: 'round' });
    } else {
      // Tænker: øjnene kigger op mod højre.
      g.ellipse(sx + 1.2, -3.2, 3, 3.6).fill(C.yellow);
      g.circle(sx + 2.2, -4.4, 1).fill(C.white);
    }
  }
}

function mouth(g: Graphics, eyes: Eyes): void {
  if (eyes === 'worried') g.moveTo(-4, 9.5).quadraticCurveTo(0, 7, 4, 9.5);
  else if (eyes === 'think') g.moveTo(-2.5, 8.5).lineTo(3, 8);
  else g.moveTo(-5, 7).quadraticCurveTo(0, 11.5, 5, 7);
  g.stroke({ width: 2, color: C.yellow, cap: 'round' });
}

/** Hele Volt, centreret om hovedets midte. Radius ca. 20. */
function volt(eyes: Eyes, withTail = true): Draw {
  return (g) => {
    if (withTail) tail(g);
    // Antenne med en lille pære.
    g.moveTo(0, -20).lineTo(0, -27).stroke({ width: 2, color: OUT, cap: 'round' });
    g.circle(0, -28.5, 3).fill(C.yellow).stroke({ width: 1.6, color: OUT });
    // Kroppen: en rund petrol kugle med et lysere skær.
    g.circle(0, 0, 20).fill(C.petrol).stroke({ width: OW, color: OUT });
    g.ellipse(-6, -10, 9, 5).fill({ color: mix(C.petrol, C.white, 0.35), alpha: 0.6 });
    // Ansigtsskærm.
    g.roundRect(-15, -10, 30, 22, 9).fill(mix(C.petrol, OUT, 0.55));
    ledEyes(g, eyes);
    mouth(g, eyes);
    // Headset: bøjle over hovedet, ørepuder og en mikrofonarm.
    g.moveTo(-19, -4).quadraticCurveTo(-18, -24, 0, -24).quadraticCurveTo(18, -24, 19, -4);
    g.stroke({ width: 3, color: OUT, cap: 'round' });
    for (const sx of [-1, 1]) g.roundRect(sx * 21 - 4, -8, 8, 12, 3).fill(C.green).stroke({ width: 1.8, color: OUT });
    g.moveTo(-21, 3).quadraticCurveTo(-20, 13, -9, 14).stroke({ width: 2, color: OUT, cap: 'round' });
    g.circle(-8, 14, 2.4).fill(C.yellow).stroke({ width: 1.4, color: OUT });
  };
}

/** Markøren på kortet: en lille Volt i en petrol ring, der peger på stedet han vil bygge. */
const marker: Draw = (g) => {
  g.circle(0, 0, 15).fill({ color: C.white, alpha: 0.92 }).stroke({ width: 2.5, color: C.petrol });
  g.poly([-5, 13, 5, 13, 0, 21], true).fill(C.petrol);
  g.circle(0, 0, 10).fill(C.petrol).stroke({ width: 1.6, color: OUT });
  for (const sx of [-3.6, 3.6]) g.ellipse(sx, -1, 1.7, 2.1).fill(C.yellow);
  g.moveTo(-2.4, 4).quadraticCurveTo(0, 6, 2.4, 4).stroke({ width: 1.3, color: C.yellow, cap: 'round' });
};

export const VOLT_DRAWS: Record<string, Draw> = {
  volt: volt('happy'),
  volt_think: volt('think'),
  volt_worried: volt('worried'),
  volt_blink: volt('blink'),
  volt_face: volt('happy', false),
  volt_marker: marker,
};
